<template>
    <input-autocomplete
        v-bind="
            getInputProps({
                onSelect: onAutocompleteSelect,
                onKeyDown: onInputWrite,
                onBlur: onInputBlur,
            })
        "
        v-model="inputValue"
        ref="input"
    >
        <template #beforeInput>
            <div
                class="flex flex-wrap px-2 pt-2"
                v-if="!hideTags && tags.length"
            >
                <template v-for="tag in tags">
                    <div class="py-1 pr-1">
                        <div
                            class="px-3 py-1.5 bg-gray-50 rounded-xl border border-gray-100 flex items-center gap-2 text-xs font-bold text-primary whitespace-nowrap"
                        >
                            <span>{{ tag.label }}</span>
                            <a
                                href="#"
                                class="text-gray-400 hover:text-red-500 transition-colors flex items-center"
                                @click.prevent="removeTag(tag)"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="2.5"
                                    stroke="currentColor"
                                    class="w-3 h-3"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M6 18 18 6M6 6l12 12"
                                    />
                                </svg>
                            </a>
                        </div>
                    </div>
                </template>
            </div>
        </template>

        <template #hidden="{ autocompleteList, selected, dropdownShow }">
            <slot
                name="hidden"
                v-bind="{
                    autocompleteList,
                    selected,
                    dropdownShow,
                    tags,
                    removeTag,
                    addTagFromAutocompleteItem,
                }"
            >
            </slot>
            <div style="display: none" ref="tags">
                <slot name="tags"></slot>
            </div>
        </template>
        <template #next="{ autocompleteList, selected, dropdownShow }">
            <slot
                name="next"
                v-bind="{
                    autocompleteList,
                    selected,
                    dropdownShow,
                    tags,
                    removeTag,
                    addTagFromAutocompleteItem,
                }"
            >
                <div class="flex flex-wrap gap-2 mt-2" v-if="tags.length">
                    <template v-for="tag in tags">
                        <div
                            class="px-3 py-1.5 bg-gray-50 rounded-xl border border-gray-100 flex items-center gap-2 text-xs font-bold text-gray-500 whitespace-nowrap"
                        >
                            <span>{{ tag.label }}</span>
                            <a
                                href="#"
                                class="text-gray-400 hover:text-red-500 transition-colors flex items-center"
                                @click.prevent="removeTag(tag)"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="2.5"
                                    stroke="currentColor"
                                    class="w-3 h-3"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M6 18 18 6M6 6l12 12"
                                    />
                                </svg>
                            </a>
                        </div>
                    </template>
                </div>
            </slot>
        </template>
        <template #prev="{ autocompleteList, selected, dropdownShow }">
            <slot
                name="prev"
                v-bind="{
                    autocompleteList,
                    selected,
                    dropdownShow,
                    tags,
                    removeTag,
                    addTagFromAutocompleteItem,
                }"
            >
            </slot>
        </template>
    </input-autocomplete>
</template>

<script>
import mixinCommon from "./mixinCommon.vue";
import mixinAjax from "./mixinAjax.vue";
import mixinInput from "./mixinInput.vue";

import InputAutocomplete from "./InputAutocomplete.vue";

const { props } = InputAutocomplete;

export default {
    mixins: [mixinCommon, mixinAjax, mixinInput],

    components: {
        "input-autocomplete": InputAutocomplete,
    },
    props: {
        ...props,

        hideTags: false,

        uniqueTagsBy: {
            type: String,
            default: "",
        },

        defaultTags: {
            type: Array,
            default: () => [],
        },
        maxTags: {
            type: [Number, String],
            default: null,
        },
        maxTagsMessage: {
            type: String,
            default: "You can select up to 10 items.",
        },
        linkedCheckboxSelector: {
            type: String,
            default: "",
        },
        tagsChangeEventName: {
            type: String,
            default: "",
        },
    },
    computed: {
        uniqueTags() {
            return !!this.uniqueTagsBy;
        },
        lastSelected() {
            return this.$refs.input.$data.selected;
        },
        isLimitReached() {
            const maxTags = this.getMaxTagsValue();
            if (!maxTags) {
                return false;
            }

            return this.getTotalSelectedCount() >= maxTags;
        },
    },

    data() {
        return {
            tags: this.defaultTags,
            preventBlur: false,
            limitError: "",
            linkedCheckboxHandler: null,
        };
    },
    mounted() {
        const tags = this.getJsonFromRef("tags");
        if (tags) {
            this.tags = tags;
        }

        this.bindLinkedCheckboxHandler();
        this.emitTagsChange();
    },
    beforeUnmount() {
        this.unbindLinkedCheckboxHandler();
    },
    watch: {
        tags: {
            handler() {
                if (!this.isLimitReached) {
                    this.limitError = "";
                    this.setNativeValidationMessage("");
                }
                this.emitTagsChange();
            },
            deep: true,
        },
    },
    methods: {
        bindLinkedCheckboxHandler() {
            if (!this.linkedCheckboxSelector) {
                return;
            }

            const form = this.$el.closest("form");
            if (!form) {
                return;
            }

            this.linkedCheckboxHandler = () => {
                if (!this.isLimitReached) {
                    this.limitError = "";
                    this.setNativeValidationMessage("");
                }
                this.emitTagsChange();
            };

            form.addEventListener("change", this.linkedCheckboxHandler);
        },
        unbindLinkedCheckboxHandler() {
            const form = this.$el.closest("form");
            if (form && this.linkedCheckboxHandler) {
                form.removeEventListener("change", this.linkedCheckboxHandler);
            }
        },
        getMaxTagsValue() {
            const parsed = Number(this.maxTags);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
        },
        getExternalSelectedCount() {
            if (!this.linkedCheckboxSelector) {
                return 0;
            }

            const form = this.$el.closest("form");
            if (!form) {
                return 0;
            }

            return form.querySelectorAll(
                `${this.linkedCheckboxSelector}:checked`,
            ).length;
        },
        getTotalSelectedCount() {
            return this.tags.length + this.getExternalSelectedCount();
        },
        emitTagsChange() {
            const detail = {
                tags: this.tags,
                tagCount: this.tags.length,
                externalSelectedCount: this.getExternalSelectedCount(),
                totalSelected: this.getTotalSelectedCount(),
                maxTags: this.getMaxTagsValue(),
                isAtLimit: this.isLimitReached,
            };

            this.$emit("tags-change", detail);

            if (this.tagsChangeEventName && typeof window !== "undefined") {
                window.dispatchEvent(
                    new CustomEvent(this.tagsChangeEventName, {
                        detail,
                    }),
                );
            }
        },
        setNativeValidationMessage(message = "") {
            const autocomplete = this.$refs.input;
            if (
                autocomplete &&
                typeof autocomplete.setNativeInputValidationMessage ===
                    "function"
            ) {
                autocomplete.setNativeInputValidationMessage(message);
            }
        },
        triggerLimitError() {
            this.limitError = this.maxTagsMessage;
            this.setNativeValidationMessage(this.maxTagsMessage);
            window.dispatchEvent(
                new CustomEvent("wf-notify", {
                    detail: {
                        type: "error",
                        message: this.maxTagsMessage,
                    },
                }),
            );
        },
        onInputWrite(evt) {
            switch (evt.which) {
                case 13:
                    evt.preventDefault();
                    this.preventBlur = true;
                    break;
            }
        },
        onInputBlur(evt) {
            if (this.preventBlur) {
                this.focus();
                this.preventBlur = false;
            }

            setTimeout(() => {
                const autocompleteRef = this.$refs.input;
                const hasStrictSelectionError = !!(
                    autocompleteRef &&
                    autocompleteRef.$data &&
                    autocompleteRef.$data.strictSelectionError
                );

                if (hasStrictSelectionError) {
                    return;
                }

                this.inputValue = "";
            }, 0);
        },

        onAutocompleteSelect(item) {
            this.addTagFromAutocompleteItem(item);
        },
        addTagFromAutocompleteItem(item, { clearInput = true } = {}) {
            const { text = "" } = item || {};
            const label = text.trim();

            if (!label) {
                return;
            }

            this.addTag({
                ...item,
                label,
            });

            if (clearInput) {
                this.inputValue = "";
            }
        },

        addTag(tag) {
            const maxTags = this.getMaxTagsValue();
            if (maxTags && this.getTotalSelectedCount() >= maxTags) {
                this.triggerLimitError();
                return;
            }

            if (this.uniqueTags) {
                const fieldName = this.uniqueTagsBy;
                for (let item of this.tags) {
                    const incomingValue =
                        typeof tag[fieldName] === "string"
                            ? tag[fieldName].trim().toLowerCase()
                            : tag[fieldName];
                    const existingValue =
                        typeof item[fieldName] === "string"
                            ? item[fieldName].trim().toLowerCase()
                            : item[fieldName];

                    if (incomingValue && existingValue === incomingValue) {
                        return;
                    }
                }
            }
            this.tags.push(tag);
        },
        removeTag(tagToRemove) {
            this.tags = this.tags.filter((tag) => tag !== tagToRemove);
        },

        getInputProps(overrideProps = {}) {
            return this.getComponentProps(InputAutocomplete, {
                error: this.error || this.limitError,
                ...overrideProps,
            });
        },
    },
};
</script>
